import React from 'react';
import { Box, Button, Chip, Grid, InputLabel, List, ListItem, ListItemIcon, ListItemText, MenuItem, OutlinedInput, Select } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FolderOffIcon from '@mui/icons-material/FolderOff';
import { FormDrawerGroup } from '../../../../components/form-drawer';
import { EmptyState } from '../../../../components/empty-state';
import { FieldError } from '../../../../components/form-page-parts';
import { RowAction } from '../../../../components/row-action';
import { ISubmissionPublicationDetailResponse } from '../../../../model/sep/submission-publications/response/submission-publication-detail-response';
import { ISubmissionPublicationFileResponse } from '../../../../model/sep/submission-publications/response/submission-publication-file-response';
import { ISubmissionPublicationUpdateRequest } from '../../../../model/sep/submission-publications/request/submission-publication-update-request';
import { IStatusSubmissionPublicationResponse } from '../../../../model/sep/submission-publications/response/status-submission-publication-response';
import { IPublishingVehicleResponse } from '../../../../model/sdp/official-daily/response/publishing-vehicle-response';
import { formatDate, formatDateTime } from '../../../../utils/date';

function ReadOnlyField({ label, value, multiline }: { label: string; value?: string | number; multiline?: boolean }) {
    return (
        <React.Fragment>
            <InputLabel>{label}</InputLabel>
            <OutlinedInput fullWidth disabled multiline={multiline} rows={multiline ? 4 : undefined} value={value ?? ''} />
        </React.Fragment>
    );
}

export function SubmissionInfoSection({ detail }: { detail?: ISubmissionPublicationDetailResponse }) {
    return (
        <FormDrawerGroup title="Dados da remessa">
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <ReadOnlyField label="Enviada em" value={formatDateTime(detail?.sendDate)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <ReadOnlyField label="Publicar em" value={formatDate(detail?.publishDate)} />
                </Grid>
                <Grid item xs={12}>
                    <ReadOnlyField label="Título" value={detail?.title} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <ReadOnlyField label="Enviada por" value={detail?.userSenderName} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <ReadOnlyField label="Entidade" value={detail?.clientName} />
                </Grid>
                <Grid item xs={12}>
                    <ReadOnlyField label="Observação/instrução do remetente" value={detail?.instruction} multiline />
                </Grid>
            </Grid>
        </FormDrawerGroup>
    );
}

export function SubmissionFilesSection({ files, onDownload, onDownloadAll }: {
    files: ISubmissionPublicationFileResponse[];
    onDownload: (file: ISubmissionPublicationFileResponse) => void;
    onDownloadAll: () => void;
}) {
    return (
        <FormDrawerGroup title="Arquivos enviados">
            {files.length === 0 ? (
                <EmptyState icon={<FolderOffIcon />} message="Nenhum arquivo enviado nesta remessa." />
            ) : (
                <React.Fragment>
                    <List dense disablePadding>
                        {files.map((file) => (
                            <ListItem key={file.id} disableGutters secondaryAction={
                                <RowAction title="Baixar arquivo" onClick={() => onDownload(file)}>
                                    <DownloadIcon fontSize="small" />
                                </RowAction>
                            }>
                                <ListItemIcon sx={{ minWidth: 36 }}><InsertDriveFileIcon fontSize="small" /></ListItemIcon>
                                <ListItemText primary={file.name} />
                            </ListItem>
                        ))}
                    </List>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onDownloadAll}>Baixar todos</Button>
                    </Box>
                </React.Fragment>
            )}
        </FormDrawerGroup>
    );
}

export function SubmissionVehiclesSection({ vehicles }: { vehicles?: IPublishingVehicleResponse[] }) {
    return (
        <FormDrawerGroup title="Veículos de publicação">
            {!vehicles || vehicles.length === 0 ? (
                <EmptyState message="Nenhum veículo de publicação informado." />
            ) : (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {vehicles.map((vehicle) => <Chip key={vehicle.id} label={vehicle.name} variant="outlined" />)}
                </Box>
            )}
        </FormDrawerGroup>
    );
}

export function SubmissionReviewSection({ form, status, statusError, onChange }: {
    form: ISubmissionPublicationUpdateRequest;
    status: IStatusSubmissionPublicationResponse[];
    statusError?: string;
    onChange: (changes: ISubmissionPublicationUpdateRequest) => void;
}) {
    return (
        <FormDrawerGroup title="Análise da diagramação">
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <InputLabel required>Status</InputLabel>
                    <Select fullWidth displayEmpty value={form.statusId ?? ''} error={!!statusError} onChange={(e) => onChange({ statusId: e.target.value === '' ? undefined : Number(e.target.value) })}>
                        <MenuItem value=""><em>Selecione</em></MenuItem>
                        {status.map((item) => <MenuItem key={item.id} value={item.id}>{item.description}</MenuItem>)}
                    </Select>
                    <FieldError message={statusError} />
                </Grid>
                <Grid item xs={12}>
                    <InputLabel>Observações/pendências</InputLabel>
                    <OutlinedInput fullWidth multiline rows={5} value={form.diagramerObservation ?? ''} onChange={(e) => onChange({ diagramerObservation: e.target.value })} />
                </Grid>
            </Grid>
        </FormDrawerGroup>
    );
}
